import React from 'react';
import ServicePageTemplate from './ServicePageTemplate';
import { Droplet, Repeat, Clock, Users, ShieldCheck, MapPin } from 'lucide-react';

const DialysisTransportation = () => {
  const seo = {
    title: 'Dialysis Transportation Richmond TX & Greater Houston | MoveraCare',
    description: 'Recurring dialysis transportation serving Richmond TX, Fort Bend County, and Greater Houston. Reliable standing schedules to and from dialysis appointments; wheelchair and ambulatory options. Call 832-925-2128.',
    path: '/services/dialysis-transportation',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: 'Dialysis Transportation',
      serviceType: 'Non-Emergency Medical Transportation for Dialysis',
      areaServed: [
        { '@type': 'City', name: 'Richmond, TX' },
        { '@type': 'City', name: 'Sugar Land, TX' },
        { '@type': 'City', name: 'Missouri City, TX' },
        { '@type': 'City', name: 'Katy, TX' },
        { '@type': 'AdministrativeArea', name: 'Fort Bend County, Texas' },
        { '@type': 'AdministrativeArea', name: 'Harris County, Texas' },
      ],
      provider: {
        '@type': 'MedicalBusiness',
        name: 'MoveraCare Mobility',
        legalName: 'MoveraCare Services LLC',
        telephone: '+1-832-925-2128',
        email: 'dispatch@moveracaremobility.com',
        url: 'https://www.moveracaremobility.com',
        address: { '@type': 'PostalAddress', addressLocality: 'Richmond', addressRegion: 'TX', addressCountry: 'US' },
      },
    },
  };

  return (
    <ServicePageTemplate
      testid="service-dialysis"
      seo={seo}
      eyebrow="DIALYSIS TRANSPORTATION · RICHMOND, TX · FORT BEND COUNTY"
      heading="Dialysis Transportation in Richmond TX and Greater Houston"
      intro="MoveraCare Mobility is based in Richmond, TX and provides recurring wheelchair and ambulatory dialysis transportation across Fort Bend County, Harris County, and Greater Houston — including Richmond, Sugar Land, Missouri City, Katy, Rosenberg, Stafford, and Fulshear. Standing weekly schedules are supported to and from dialysis appointments."
      who={{
        title: 'For Dialysis Patients, Families, and Care Coordinators',
        bullets: [
          'Patients on standing dialysis treatment schedules',
          'Families arranging weekly dialysis transportation',
          'Dialysis center patient care coordinators and social workers',
          'Case managers coordinating dialysis benefits',
          'Nephrology practices and clinic coordinators',
          'Skilled nursing facilities coordinating resident dialysis trips',
        ],
      }}
      included={{
        title: 'Dialysis Transportation Includes',
        bullets: [
          'Recurring standing schedules aligned to treatment days',
          'Wheelchair-accessible or ambulatory vehicles based on mobility',
          'Pickup and return coordination around treatment start and end times',
          'Wait-and-return or same-day return based on schedule',
          'Consistent driver assignments when possible for continuity',
          'Coordination with dialysis center scheduling teams',
        ],
      }}
      howItWorks={{
        title: 'Setting Up Dialysis Transportation',
        steps: [
          { title: 'Patient Profile', desc: 'Share patient mobility level, address, treatment schedule, and any authorization info.' },
          { title: 'Standing Schedule', desc: 'MoveraCare configures a recurring schedule aligned to dialysis days and appointment windows.' },
          { title: 'Ongoing Trips', desc: 'Pickups and returns run on a consistent recurring schedule with operational communication.' },
          { title: 'Adjustments', desc: 'Schedule changes or ad-hoc trips can be requested through dispatch as needed.' },
        ],
      }}
      why={{
        title: 'Why Dialysis Patients Choose MoveraCare',
        items: [
          { icon: Repeat, title: 'Built for Recurring Care', desc: 'Operations designed around standing weekly schedules.' },
          { icon: Clock, title: 'Treatment-Aligned Punctuality', desc: 'Focus on the on-time performance dialysis schedules require.' },
          { icon: Droplet, title: 'Dialysis-Specific Coordination', desc: 'Pickup and return timing aligned to treatment realities.' },
          { icon: Users, title: 'Driver Assistance', desc: 'Drivers assist passengers during transport, on every trip.' },
          { icon: ShieldCheck, title: 'HIPAA-Conscious', desc: 'Professional handling of patient information across operations.' },
          { icon: MapPin, title: 'Greater Houston Coverage', desc: 'Service across Fort Bend and surrounding counties.' },
        ],
      }}
      faqs={[
        { question: 'Do you support 3-day-per-week dialysis schedules?', answer: 'Yes. Monday-Wednesday-Friday, Tuesday-Thursday-Saturday, and other recurring schedules are supported.' },
        { question: 'Can the driver return me home after dialysis?', answer: 'Yes. Return transportation is coordinated as part of the standing schedule or as arranged with dispatch.' },
        { question: 'Do you provide wheelchair transportation to dialysis?', answer: 'Yes. Wheelchair-accessible vehicles are available for patients who remain in their wheelchair for transport.' },
        { question: 'How do I set up a recurring dialysis schedule?', answer: 'Call dispatch at (832) 925-2128 or submit a Request Patient Transportation form. Share the treatment schedule and mobility level.' },
      ]}
    />
  );
};

export default DialysisTransportation;
